import { useState } from 'react';
import { FolderOpen } from 'lucide-react';
import { FileExplorer } from '@speles7172/file-console';
import '@speles7172/file-console/styles.css';
import { fileTransport } from '../../api/files';
import type { VendorEventAccess } from '../../api/portal';
import { SegmentedControl } from '../common/SegmentedControl';

export interface VendorFilesPanelProps {
  folders: VendorEventAccess['folders'];
}

/**
 * The folders of an event's files the office shared with a vendor, each opened
 * from that folder down.
 *
 * The browser is the package's `FileExplorer`, rooted at the shared folder and
 * `embedded`, so there is no way up out of it — and the server would refuse
 * one anyway: what a vendor may see and do there is decided by the file
 * authorizer (a `read` share lists and downloads; an `upload` share also
 * uploads and adds folders, and changes or deletes only their own).
 */
export function VendorFilesPanel({ folders }: VendorFilesPanelProps) {
  const [chosen, setChosen] = useState(folders[0]?.nodeId ?? '');
  const current = folders.find((folder) => folder.nodeId === chosen) ?? folders[0];
  if (!current) return null;

  return (
    <div data-testid="vendor-files" className="space-y-3">
      <h3 className="font-semibold text-lg flex items-center gap-2">
        <FolderOpen size={18} />
        Files
      </h3>
      {folders.length > 1 && (
        <SegmentedControl
          variant="chips"
          ariaLabel="Shared folders"
          options={folders.map((folder) => ({ id: folder.nodeId, label: folder.name }))}
          value={current.nodeId}
          onChange={setChosen}
        />
      )}
      <p className="text-xs text-gray-500">
        {current.access === 'upload'
          ? 'You can upload here, and change or delete what you uploaded.'
          : 'You can open and download these files.'}
      </p>
      <div className="h-[28rem]">
        {/* Keyed by folder: each opens at its own top, never where the last one was left. */}
        <FileExplorer
          key={current.nodeId}
          transport={fileTransport}
          rootNodeId={current.nodeId}
          embedded
          title={null}
        />
      </div>
    </div>
  );
}
